import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"

import useCoachOnboardingStore, {
  CoachOnboardingStep,
} from "./useCoachOnboardingStore"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"

const QuizOrSkipChoice = () => {
  const {
    setStep,
    setQuestionAnswerMapping,
    setCoachQuestionnaireStep,
  } = useCoachOnboardingStore()

  const handleTakeQuiz = () => {
    // Clear any defaults the skip path may have written into the store
    // (e.g. if the parent picked "Skip", landed on the signup screen,
    // then back-nav'd here and changed their mind). Without this, the
    // multi-choice question would appear with the first answer
    // pre-selected on render.
    setQuestionAnswerMapping({})
    setCoachQuestionnaireStep(0)
    trackAnalyticEvent(ANALYTIC_EVENTS.COACH_ONBOARDING_CHOICE_TAKE_QUIZ)
    setStep(CoachOnboardingStep.COACH_QUESTIONNAIRE)
  }

  const handleSkipQuiz = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.COACH_ONBOARDING_CHOICE_SKIP_QUIZ)
    setStep(CoachOnboardingStep.COACH_SIGN_UP_SKIP)
  }

  return (
    <FlexBox
      direction="column"
      align="center"
      justify="flex-start"
      wrap={false}
      gap={SPACING.space6}
      style={{
        width: "min(450px, 100%)",
        margin: "0 auto",
        padding: `${SPACING.space6} ${SPACING.space4} 12dvh`,
      }}
    >
      <FlexBox
        direction="column"
        align="center"
        gap={SPACING.space2}
        wrap={false}
      >
        <Typography
          variant="h2Serif"
          textAlign="center"
          style={{ marginTop: SPACING.space4 }}
        >
          How would you like to start?
        </Typography>
        <Typography
          variant="body"
          textAlign="center"
          color={JoonUIColor.text.secondary}
          style={{ maxWidth: 360 }}
        >
          Take the quick quiz for a personalized match, or jump straight to
          our top-rated coaches.
        </Typography>
      </FlexBox>

      <ChoiceCard
        emoji="✨"
        title="Take the quiz"
        description="Answer a few quick questions so we can match you with the right coach for your family."
        meta="~2 min · personalized match"
        recommended
        onClick={handleTakeQuiz}
      />

      <ChoiceCard
        emoji="⚡"
        title="Skip the quiz"
        description="Jump straight to our top-rated coaches. You can always come back to personalize later."
        meta="Fastest path · see coaches now"
        onClick={handleSkipQuiz}
      />
    </FlexBox>
  )
}

interface ChoiceCardProps {
  emoji: string
  title: string
  description: string
  meta: string
  recommended?: boolean
  onClick: () => void
}

const ChoiceCard = ({
  emoji,
  title,
  description,
  meta,
  recommended,
  onClick,
}: ChoiceCardProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: "unset",
        cursor: "pointer",
        width: "100%",
        position: "relative",
        padding: SPACING.space5,
        background: "rgba(255, 255, 255, 0.85)",
        border: `1.5px solid ${
          recommended
            ? JoonColorExpanded.viridian300
            : JoonColorExpanded.neutral200
        }`,
        borderRadius: SPACING.space2,
        boxShadow: recommended
          ? "0 6px 16px rgba(122, 123, 224, 0.12)"
          : "0 2px 8px rgba(0, 0, 0, 0.04)",
        transition: "transform 0.12s ease, box-shadow 0.12s ease",
        boxSizing: "border-box",
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "scale(0.985)"
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)"
      }}
    >
      {recommended && (
        <span
          style={{
            position: "absolute",
            top: -10,
            right: SPACING.space4,
            background: JoonColorExpanded.viridian300,
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            padding: `2px ${SPACING.space2}`,
            borderRadius: 999,
            textTransform: "uppercase",
          }}
        >
          Recommended
        </span>
      )}
      <FlexBox direction="row" align="flex-start" gap={SPACING.space3} wrap={false}>
        <span style={{ fontSize: 28, lineHeight: 1 }} aria-hidden="true">
          {emoji}
        </span>
        <FlexBox direction="column" gap={SPACING.space1} wrap={false}>
          <Typography variant="bodyBold" color={JoonUIColor.text.primary}>
            {title}
          </Typography>
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            {description}
          </Typography>
          <Typography
            variant="bodyXSmall"
            color={JoonUIColor.text.secondary}
            style={{
              marginTop: SPACING.space1,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            {meta}
          </Typography>
        </FlexBox>
      </FlexBox>
    </button>
  )
}

export default QuizOrSkipChoice
